/**
 * Whisper
 *
 * A lightweight and customizable React Snackbar (toast) component built with framer-motion and tailwindcss.
 *
 * Github: https://github.com/farzany/whisper
 */

import React, { createContext, useContext, useState, ReactNode, useCallback, MouseEventHandler } from 'react';
import { Alert, AlertColor, Box, Button, IconButton, Snackbar, Stack, useMediaQuery, useTheme } from '@mui/material';
import { XMarkIcon } from '@heroicons/react/24/outline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

const DEFAULT_MAX_SNACKS = 6;
const DEFAULT_DURATION = 6000;

export type SnackType = 'success' | 'warning' | 'error' | 'info' | 'default';
export type Position = 'bottom-left' | 'bottom-center' | 'bottom-right' | 'top-left' | 'top-center' | 'top-right';

const POSITION_CLASSES: Record<Position, { vertical: 'top' | 'bottom', horizontal: 'left' | 'center' | 'right' }> = {
  'bottom-left': { vertical: 'bottom', horizontal: 'left' },
  'bottom-center': { vertical: 'bottom', horizontal: 'center' },
  'bottom-right': { vertical: 'bottom', horizontal: 'right' },
  'top-left': { vertical: 'top', horizontal: 'left' },
  'top-center': { vertical: 'top', horizontal: 'center' },
  'top-right': { vertical: 'top', horizontal: 'right' },
};

interface Snack {
  id: string;
  message: string;
  type?: SnackType;
  duration: number;
  description?: string;
  icon?: ReactNode;
  dismissable?: boolean;
  action?: { label: string, onClick: MouseEventHandler<HTMLButtonElement>, icon?: ReactNode };
}

interface SnackOptions {
  duration?: number;
  description?: string;
  icon?: ReactNode;
  dismissable?: boolean;
  action?: { label: string, onClick: MouseEventHandler<HTMLButtonElement>, icon?: ReactNode };
}

interface PromiseSnackOptions {
  loading: { message: string, type?: SnackType, options?: SnackOptions },
  success: { message: string, type?: SnackType, options?: SnackOptions },
  error?: { message?: string, type?: SnackType, options?: SnackOptions },
}

interface SnackbarContextType {
  snackbar: {
    (_message: string, _options?: SnackOptions): string;
    info: (_message: string, _options?: SnackOptions) => string;
    success: (_message: string, _options?: SnackOptions) => string;
    warning: (_message: string, _options?: SnackOptions) => string;
    error: (_message: string, _options?: SnackOptions) => string;
    promise: <T>(_promise: Promise<T>, _options: PromiseSnackOptions) => Promise<T>;
    dismiss: (_ids?: string[]) => void;
  };
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
  maxSnacks?: number;
  position?: Position;
}

export const SnackbarProvider = ({ children, maxSnacks = DEFAULT_MAX_SNACKS }: SnackbarProviderProps) => {
  const [snacks, setSnacks] = useState<Snack[]>([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const createSnack = useCallback(
    (message: string, type: Snack['type'] = 'default', options: SnackOptions = {}) => {
      const { duration = DEFAULT_DURATION, icon = null, description, dismissable = false, action } = options;
      const id = crypto.randomUUID();

      const newSnack = { id, message, type, duration, description, icon, dismissable, action };
      setSnacks((prev) => {
        const updated = [...prev, newSnack];
        if (updated.length > maxSnacks) {
          return updated.slice(-maxSnacks);
        }
        return updated;
      });

      return id;
    }, [maxSnacks]);

  const snackbar = Object.assign(
    (message: string, options?: SnackOptions) => createSnack(message, 'default', options),
    {
      info: (message: string, options?: SnackOptions) => createSnack(message, 'info', options),
      success: (message: string, options?: SnackOptions) => createSnack(message, 'success', options),
      warning: (message: string, options?: SnackOptions) => createSnack(message, 'warning', options),
      error: (message: string, options?: SnackOptions) => createSnack(message, 'error', options),
      promise: async function <T>(promise: Promise<T>, options: PromiseSnackOptions): Promise<T> {
        const { loading, success, error } = options;
        const id = createSnack(loading.message, loading.type ?? 'default', { ...loading.options, duration: 999999 });
        try {
          const result = await promise;
          const type = success.type ?? 'success';
          setSnacks((prev) => prev.map((snack) => snack.id === id ? { ...snack, message: success.message, type, duration: DEFAULT_DURATION, ...success.options } : snack));
          return result;
        } catch (err) {
          const type = error?.type ?? 'error';
          setSnacks((prev) => prev.map((snack) => snack.id === id ? { ...snack, message: error?.message ?? 'Something went wrong. Please try again.', type, duration: DEFAULT_DURATION, ...error?.options } : snack));
          throw err;
        }
      },
      dismiss: (ids?: string[]) => {
        setSnacks((prev) => ids ? prev.filter((snack) => !ids.includes(snack.id)) : []);
      },
    },
  );

  const handleClose = (id: string) => (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnacks((prev) => prev.filter((snack) => snack.id !== id));
  };

  const getMuiSeverity = (type?: SnackType): AlertColor => {
    switch (type) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  };

  const getDefaultIcon = (type?: SnackType): ReactNode => {
    switch (type) {
      case 'success':
        return theme.components?.MuiAlert?.defaultProps?.iconMapping?.success;
      case 'warning':
        return theme.components?.MuiAlert?.defaultProps?.iconMapping?.warning;
      case 'error':
        return theme.components?.MuiAlert?.defaultProps?.iconMapping?.error;
      case 'info':
        return theme.components?.MuiAlert?.defaultProps?.iconMapping?.info;
      default:
        return theme.components?.MuiAlert?.defaultProps?.iconMapping?.info;
    }
  };

  return (
    <SnackbarContext.Provider value={{ snackbar }}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          bottom: isSmallScreen ? 'auto' : 16,
          top: isSmallScreen ? 'calc(var(--safe-area-inset-top) + 16px)' : 'auto',
          left: isSmallScreen ? '50%' : 16,
          transform: isSmallScreen ? 'translateX(-50%)' : 'none',
          zIndex: 100000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {snacks.map((snack, index) => (
          <Snackbar
            key={snack.id}
            open={true}
            autoHideDuration={snack.duration}
            onClose={handleClose(snack.id)}
            anchorOrigin={isSmallScreen ? { vertical: 'top', horizontal: 'center' } : { vertical: 'bottom', horizontal: 'left' }}
            sx={{ position: 'relative', transform: 'none' }}
          >
            <Alert
              severity={getMuiSeverity(snack.type)}
              variant="filled"
              icon={false}
            >
              <Stack spacing={0.5} direction="row" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" justifyContent="center" pr={1}>
                  {snack.icon || getDefaultIcon(snack.type)}
                </Box>
                <Stack spacing={1}>
                  {snack.message}
                  {snack.description && <div className="text-sm">{snack.description}</div>}
                </Stack>
                <IconButton size="small" onClick={handleClose(snack.id)} color="inherit">
                  <XMarkIcon className="size20" />
                </IconButton>
              </Stack>
              {snack.action && (
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  startIcon={snack.action?.icon}
                  sx={{ mt: 2 }}
                  fullWidth
                  onClick={(e) => {
                    snack.action?.onClick(e);
                    handleClose(snack.id)();
                  }}
                >
                  {snack.action.label}
                </Button>
              )}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};